"use client";

import MyCard from "@/app/components/MyCard";
import { useTranslations } from "next-intl";
import LinkToKlardaApp from "@/app/components/LinkToKlardaApp";
import React, { memo, Suspense } from "react";
import { BASE_URL } from "@/constant/apiPath";
import type { INewsResponse, ProductNewsResponseCDN } from "@/models/news";
import dynamic from "next/dynamic";
import Head from "next/head";
import { Divider, Skeleton } from "antd/lib";
import useAxiosQuery from "@/hooks/useMyQuery";
import { NewCard } from "@/app/components/MarketInsightCard";
import { useLanguageContext } from "@/context/LanguageContext";

const MainNews = dynamic(() => import("@/app/components/news/MainNews"), { ssr: false });
const PeopleAlsoWatch = dynamic(() => import("@/app/components/products/PeopleAlsoWatch"), { ssr: false });

const SectionCard = ({ articles }: { articles?: Array<INewsResponse> }) => (
  <div className="space-y-[5px]">
    {articles?.map((item, index) => (
      <div key={index} className="mycard-hover">
        <MyCard styles={{ body: { padding: 0 } }}>
          <NewCard article={item} index={index} />
        </MyCard>
        {index < articles.length - 1 && <Divider className="my-2" />}
      </div>
    ))}
  </div>
);

const SEOHead = () => {
  const { language } = useLanguageContext();
  return (
    <Head>
      <link rel="canonical" href={`${BASE_URL}/${language}/news`} />
      <title>Klarda Blogs - All About Crypto, Crypto News, Blockchain, Bitcoin</title>
      <meta
        name="description"
        content="Discover Klarda Blog to explore all about crypto, the latest crypto news; comprehensive technical and fundamental analysis; market insights; and regular recaps and reports. Enjoy a seamless user experience with our intuitive interface."
      />
      <meta name="image" content={`${BASE_URL}/seo/fb.jpg`} />
      <meta content={BASE_URL} property="og:url" />
      <meta content="website" property="og:type" />
      <meta content="Klarda: Crypto and Portfolio Intelligence" property="og:site_name" />
      <meta content={`${BASE_URL}/seo/fb.jpg`} property="og:image" />
      <meta content={BASE_URL} property="twitter:url" />
      <meta content="summary_large_image" property="twitter:card" />
      <meta content="Klarda: Crypto and Portfolio Intelligence" property="twitter:site" />
      <meta content={`${BASE_URL}/seo/twitter.jpg`} property="twitter:image" />
    </Head>
  );
};

const ProductNews = memo(({ symbol }: { symbol: string }) => {
  const t = useTranslations("news");
  const { language } = useLanguageContext();

  const { data: newsData, isLoading } = useAxiosQuery<{ data: ProductNewsResponseCDN }>({
    url: "v1/market-insight/by-symbol",
    params: { symbol },
  });

  return (
    <>
      <SEOHead />
      <main className="min-h-screen">
        <div className="flex flex-col gap-4">
          <MyCard title={t("news_analysis")}>
            {isLoading ? (
              <Skeleton active paragraph={{ rows: 8 }} />
            ) : (
              <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
                <div className="col-span-1 lg:col-span-3">
                  <MyCard styles={{ body: { padding: 0 } }}>
                    <SectionCard articles={newsData?.data?.newsAndAnalysis?.slice(1, 5)} />
                    <LinkToKlardaApp
                      isOnlyText
                      url={`/${language}/news/search?name=${symbol}`}
                      className="mt-6 block"
                    />
                  </MyCard>
                </div>
                <div className="col-span-1 lg:col-span-6">
                  <Suspense fallback={<Skeleton active />}>
                    <MainNews imageClassName="max-h-[450px]" news={newsData?.data?.newsAndAnalysis?.[0]} />
                  </Suspense>
                </div>
                <div className="col-span-1 lg:col-span-3">
                  <MyCard styles={{ body: { padding: 0 } }}>
                    <SectionCard articles={newsData?.data?.latest} />
                    <LinkToKlardaApp
                      isOnlyText
                      url={`/${language}/news/search?name=${symbol}`}
                      className="mt-6 block"
                    />
                  </MyCard>
                </div>
              </div>
            )}
          </MyCard>
          <Suspense fallback={<Skeleton active />}>
            <PeopleAlsoWatch symbol={symbol} />
          </Suspense>
        </div>
      </main>
    </>
  );
});

ProductNews.displayName = "ProductNews";
export default ProductNews;
